/**
 * ArtistSlider - Horizontal slider for displaying artists on detail pages
 * 
 * Features:
 * - Groups artists by name to avoid duplicates
 * - Combines professions for same artist
 * - Sorts by max priority descending
 * - D-pad navigation support
 */
import React, { useCallback, useRef, useMemo, memo } from "react";
import styled from "styled-components";
import { FOCUS_BORDER } from "../../config/focusStyles";
import {
    FocusContext,
    useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useNavigate } from "react-router-dom";
import { getCMSImageURL } from "../../utils/cms.utils";
import { useInputMode } from "../../utils/hooks/useInputMode";

// ============================================================================
// STYLED COMPONENTS (MUI-Free for legacy TV compatibility)
// ============================================================================

var SliderWrapper = styled.div`
  position: relative;
  width: 95%;
  margin-right: 5%;
  margin-bottom: 24px;
`;

var SliderTitle = styled.div`
  color: #D9D9D9;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 16px;
  font-family: 'AnjomanMax', sans-serif;
`;

var SliderScrollWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  padding-left: 60px;
`;

var SliderContent = styled.div`
  display: flex;
  gap: 16px;
`;

var ArtistCard = styled.div`
  min-width: 150px;
  cursor: pointer;
`;

var ArtistImageWrapper = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 12px;
  border: ${function (props) {
        return props.$focused
            ? FOCUS_BORDER.active
            : FOCUS_BORDER.inactive;
    }};
`;

var ArtistImage = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-color: #333;
`;

var ArtistPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #2A2A2A 0%, #1A1A1A 100%);
  display: table;
`;

var PlaceholderInner = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: center;
  color: #666;
  font-size: 48px;
`;

var ArtistName = styled.div`
  color: ${function (props) { return props.$focused ? "#FFFFFF" : "#D9D9D9"; }};
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  font-family: 'AnjomanMax', sans-serif;
  margin-bottom: 4px;
`;

var ArtistProfession = styled.div`
  color: #999;
  font-size: 14px;
  font-weight: 400;
  text-align: center;
  font-family: 'AnjomanMax', sans-serif;
`;

// ============================================================================
// ARTIST CARD COMPONENT
// ============================================================================

var ArtistItem = memo(function (props) {
    var artist = props.artist;
    var onFocus = props.onFocus;

    var navigate = useNavigate();

    var handleNavigate = function () {
        if (artist.slug) {
            navigate("/artists/" + artist.slug);
        }
    };

    var focusableResult = useFocusable({
        onFocus: onFocus,
        onEnterPress: handleNavigate,
    });

    var focused = focusableResult.focused;
    var ref = focusableResult.ref;
    var focusSelf = focusableResult.focusSelf;

    var inputModeResult = useInputMode();
    var setMouseFocus = inputModeResult.setMouseFocus;
    var handleMouseEnter = useCallback(function () {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    // Memoize image URL to prevent recalculation on each render
    var imageUrl = useMemo(function () {
        return artist.image ? getCMSImageURL(artist.image, {
            format: 'jpeg',
            quality: 85,
            width: 250
        }) : null;
    }, [artist.image]);

    var name = artist.farsiName || artist.englishName || "";
    var initial = name.charAt(0).toUpperCase();

    // Memoize image style to prevent re-renders from regenerating the background-image
    var imageStyle = useMemo(function () {
        return imageUrl ? { backgroundImage: "url(" + imageUrl + ")" } : undefined;
    }, [imageUrl]);

    return (
        <ArtistCard ref={ref} onClick={handleNavigate} onMouseEnter={handleMouseEnter}>
            <ArtistImageWrapper $focused={focused}>
                {imageUrl ? (
                    <ArtistImage style={imageStyle} />
                ) : (
                    <ArtistPlaceholder>
                        <PlaceholderInner>{initial}</PlaceholderInner>
                    </ArtistPlaceholder>
                )}
            </ArtistImageWrapper>
            <ArtistName $focused={focused}>{name}</ArtistName>
            {artist.profession && (
                <ArtistProfession>{artist.profession}</ArtistProfession>
            )}
        </ArtistCard>
    );
});

// ============================================================================
// HELPER: Process artists with priority sorting
// ============================================================================

/**
 * Groups artists by name, combines professions, and sorts by max priority
 * @param {Array} rawArtists - Raw artist data from API
 * @returns {Array} - Processed and sorted artist list
 */
var processArtists = function (rawArtists) {
    if (!rawArtists || !Array.isArray(rawArtists) || rawArtists.length === 0) {
        return [];
    }

    // Group by name (to avoid duplicates like same actor with different roles)
    var groupedArtists = {};

    rawArtists.forEach(function (item) {
        var artistData = item.artist;
        if (!artistData || !artistData.meta || !artistData.meta.bin) return;

        var bin = artistData.meta.bin;
        var name = (bin.farsiName && bin.farsiName.trim()) ||
            (bin.englishName && bin.englishName.trim()) ||
            artistData.id;

        if (!name) return;

        if (!groupedArtists[name]) {
            groupedArtists[name] = {
                entries: [],
                maxPriority: 0,
            };
        }

        groupedArtists[name].entries.push(item);

        // Track max priority
        var priority = item.priority || 0;
        if (priority > groupedArtists[name].maxPriority) {
            groupedArtists[name].maxPriority = priority;
        }
    });

    // Build final artist list
    var artists = [];

    Object.keys(groupedArtists).forEach(function (name) {
        var group = groupedArtists[name];
        if (group.entries.length === 0) return;

        var firstEntry = group.entries[0];
        var artistData = firstEntry.artist;
        var bin = artistData.meta.bin;

        // Combine all professions
        var professions = [];
        group.entries.forEach(function (entry) {
            var profName = entry.profession && entry.profession.name;
            if (profName && profName.trim() && professions.indexOf(profName) === -1) {
                professions.push(profName);
            }
        });

        artists.push({
            id: artistData.id,
            slug: bin.slug,
            farsiName: bin.farsiName,
            englishName: bin.englishName,
            image: bin.image,
            profession: professions.join("، "),
            priority: group.maxPriority,
        });
    });

    // Sort by priority descending
    artists.sort(function (a, b) {
        return (b.priority || 0) - (a.priority || 0);
    });

    return artists;
};

// ============================================================================
// MAIN SLIDER COMPONENT
// ============================================================================

var ArtistSlider = function (props) {
    var rawArtists = props.artists;
    var onFocus = props.onFocus;
    var title = props.title || "هنرمندان";

    var focusableResult = useFocusable({
        onFocus: onFocus,
        isFocusBoundary: true,
        focusBoundaryDirections: ["left"],
    });

    var ref = focusableResult.ref;
    var focusKey = focusableResult.focusKey;

    var innerRef = useRef(null);

    var inputModeResult = useInputMode();
    var isMouseFocus = inputModeResult.isMouseFocus;
    var isMouseFocusRef = inputModeResult.isMouseFocusRef;

    // Process artists with grouping and priority sorting
    var artists = useMemo(function () {
        return processArtists(rawArtists);
    }, [rawArtists]);

    var onAssetFocus = useCallback(function (focusProps) {
        if (isMouseFocusRef.current) return; // Skip horizontal scroll on mouse hover
        var left = focusProps.left;
        if (left < 200 && innerRef.current) {
            innerRef.current.style.transform = "translateX(" + Math.abs(left - 300) + "px)";
        } else if (innerRef.current) {
            innerRef.current.style.transform = "translateX(0px)";
        }
    }, [isMouseFocusRef]);

    // Don't render if no artists
    if (artists.length === 0) {
        return null;
    }

    return (
        <FocusContext.Provider value={focusKey}>
            <SliderWrapper ref={ref}>
                <SliderTitle>{title}</SliderTitle>
                <SliderScrollWrapper>
                    <SliderContent ref={innerRef}>
                        {artists.map(function (artist, index) {
                            return (
                                <ArtistItem
                                    key={artist.slug || artist.id || index}
                                    artist={artist}
                                    onFocus={onAssetFocus}
                                />
                            );
                        })}
                    </SliderContent>
                </SliderScrollWrapper>
            </SliderWrapper>
        </FocusContext.Provider>
    );
};

export default ArtistSlider;
